import React from 'react';
import { useFeature } from '../../ui/FeaturesProvider';
import { DownloadsAllowed } from '../../ui-kit';

/**
 * A board opened from a shared link rather than inside the workspace.
 *
 * The board pages are the same pages either way; this says which, so they
 * leave out what a viewer must not have: the chat (it can query beyond the
 * board), refreshing from the warehouse, the Share button, and downloads when
 * the link does not allow them. Where the board's calls go is set separately
 * (`setSharedBoardTarget` in manualDashboardService).
 */
interface SharedBoard {
  shared: boolean;
  /** Who is looking, for the page to say so. */
  viewer?: string;
}

const SharedBoardContext = React.createContext<SharedBoard>({ shared: false });

export function SharedBoardProvider({ viewer, allowExport, children }: {
  viewer: string;
  allowExport: boolean;
  children: React.ReactNode;
}) {
  const value = React.useMemo(() => ({ shared: true, viewer }), [viewer]);
  return (
    <SharedBoardContext.Provider value={value}>
      <DownloadsAllowed value={allowExport}>{children}</DownloadsAllowed>
    </SharedBoardContext.Provider>
  );
}

export function useSharedBoard(): SharedBoard {
  return React.useContext(SharedBoardContext);
}

/**
 * Whether the board may be drawn. Inside the workspace, the workspace's
 * "Manual Dashboards" feature decides; from a shared link the server already
 * decided, by opening the link.
 */
export function useBoardFeature(): { enabled: boolean; loading: boolean } {
  const feature = useFeature('manual_dashboards');
  const { shared } = useSharedBoard();
  return shared ? { enabled: true, loading: false } : feature;
}
